import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  AlertTriangle,
  Activity,
  Archive,
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  EyeOff,
  ExternalLink,
  Layers3,
  RefreshCw,
  RotateCcw,
  Search,
  X,
} from "lucide-react";
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import axiosInstance from "../axiosinstance";
import API_URL from "../Config";
import "./FlangeDashboard.css";

const getSelectedProject = () => {
  const stored = localStorage.getItem("selectedProject");
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored);
    return parsed?.id ?? parsed ?? null;
  } catch {
    return stored;
  }
};

const number = (value) => Number(value) || 0;
const count = (value) => number(value).toLocaleString("en-IN");
const PDF_LOGO_SRC = "/jecon.png";
const loadImageElement = (src) =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });
const formatPdfTimestamp = (date) =>
  new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);
const formatGroupLabel = (value) => {
  const label = String(value || "");
  return label.length > 22 ? `${label.slice(0, 22)}…` : label;
};

const LIFECYCLE_STAGES = [
  ["blinded", "Blinding", "#2563EB"],
  ["deblinded", "Deblinding", "#DC2626"],
  ["tightening", "Tightening", "#EAB308"],
  ["tested", "Testing", "#16A34A"],
  ["retest", "Retesting", "#F97316"],
  ["client_approved", "Client Approved", "#7C3AED"],
  ["not_started", "Not Started", "#CBD5E1"],
];

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export default function FlangeDashboard({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const reportRef = useRef(null);
  const equipmentSearchSequence = useRef(0);
  const [projectId, setProjectId] = useState(getSelectedProject);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [pdfProgress, setPdfProgress] = useState({ percent: 0, label: "" });
  const [hoveredLifecycleKey, setHoveredLifecycleKey] = useState(null);
  const [equipmentNumber, setEquipmentNumber] = useState("");
  const [equipmentSummary, setEquipmentSummary] = useState(null);
  const [equipmentSearchLoading, setEquipmentSearchLoading] = useState(false);
  const [equipmentSearchError, setEquipmentSearchError] = useState("");
  const [searchedEquipmentNumber, setSearchedEquipmentNumber] = useState("");

  const loadDashboard = useCallback(async () => {
    if (!projectId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await axiosInstance.get(
        `${API_URL}/api/dashboard/flange-overview/${projectId}`,
      );
      setData(response.data || null);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error ||
          requestError?.response?.data?.details ||
          "Unable to load flange dashboard.",
      );
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadDashboard();
    setEquipmentNumber("");
    setEquipmentSummary(null);
    setEquipmentSearchError("");
    setSearchedEquipmentNumber("");
  }, [loadDashboard]);

  useEffect(() => {
    const syncProject = () => setProjectId(getSelectedProject());
    window.addEventListener("projectChanged", syncProject);
    window.addEventListener("storage", syncProject);
    return () => {
      window.removeEventListener("projectChanged", syncProject);
      window.removeEventListener("storage", syncProject);
    };
  }, []);

  const summary = data?.summary || {};
  const lifecycle = useMemo(() => data?.lifecycle_kpi || {}, [data]);
  const activeTotal = number(summary.active_flanges);

  const lifecycleRows = useMemo(() => {
    const rows = LIFECYCLE_STAGES.map(([key, label, color]) => ({
      key,
      label,
      color,
      value: number(lifecycle[key]),
    }));
    const lifecycleTotal = rows.reduce((sum, row) => sum + row.value, 0);
    return rows.map((row) => ({
      ...row,
      percentage: lifecycleTotal ? (row.value / lifecycleTotal) * 100 : 0,
    }));
  }, [lifecycle]);
  const visibleLifecycleRows = useMemo(
    () => lifecycleRows.filter((stage) => stage.value > 0),
    [lifecycleRows],
  );
  const hoveredLifecycle = lifecycleRows.find(
    (stage) => stage.key === hoveredLifecycleKey,
  );

  const groupRows = useMemo(
    () =>
      (data?.equipment_group_kpi || []).slice(0, 12).map((group) => ({
        name: group.equipment_group_name,
        count: number(group.flange_count),
      })),
    [data],
  );
  const recentActivities = useMemo(
    () => (Array.isArray(data?.recent_activities) ? data.recent_activities : []),
    [data],
  );

  const metrics = [
    { label: "Total flanges", value: summary.total_flanges, icon: Layers3 },
    { label: "Active scope", value: summary.active_flanges, icon: Activity },
    { label: "Client approved", value: summary.completed_flanges, icon: BadgeCheck },
    { label: "Blinded", value: summary.blinded_flanges, icon: EyeOff },
    { label: "Reopened", value: summary.reopened_flanges, icon: RotateCcw },
    { label: "Out of scope", value: summary.deleted_flanges, icon: Archive },
  ];

  const openFlangeInList = (flangeNumber) => {
    const params = new URLSearchParams();
    if (flangeNumber) params.set("flangeNumber", flangeNumber);
    navigate(`/flanges${params.toString() ? `?${params.toString()}` : ""}`);
  };

  useEffect(() => {
    const equipNo = equipmentNumber.trim();
    const sequence = ++equipmentSearchSequence.current;

    if (!equipNo || !projectId) {
      setEquipmentSummary(null);
      setEquipmentSearchError("");
      setSearchedEquipmentNumber("");
      setEquipmentSearchLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setEquipmentSearchLoading(true);
      setEquipmentSearchError("");
      setEquipmentSummary(null);

      try {
        const response = await axiosInstance.get(
          `${API_URL}/api/dashboard/flange-overview/${projectId}`,
          { params: { equip_no: equipNo }, signal: controller.signal },
        );
        if (sequence !== equipmentSearchSequence.current) return;
        const payload = response.data || {};
        const summaryPayload =
          payload.equipment_summary ||
          payload.equipment_Sumarry ||
          payload.equipmentSummary;

        if (!summaryPayload) {
          setEquipmentSearchError("No equipment summary was returned.");
          setSearchedEquipmentNumber(equipNo);
          return;
        }
        setEquipmentSummary(summaryPayload);
        setSearchedEquipmentNumber(equipNo);
      } catch (requestError) {
        if (sequence !== equipmentSearchSequence.current || requestError?.code === "ERR_CANCELED") return;
        setEquipmentSearchError(
          requestError?.response?.data?.error ||
            requestError?.response?.data?.details ||
            "Unable to search this equipment number.",
        );
        setSearchedEquipmentNumber(equipNo);
      } finally {
        if (sequence === equipmentSearchSequence.current) {
          setEquipmentSearchLoading(false);
        }
      }
    }, 400);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [equipmentNumber, projectId]);

  const clearEquipmentSearch = () => {
    setEquipmentNumber("");
    setEquipmentSummary(null);
    setEquipmentSearchError("");
    setSearchedEquipmentNumber("");
  };

  const downloadReport = async () => {
    if (!reportRef.current || downloading || loading) return;
    setDownloading(true);
    setPdfProgress({ percent: 5, label: "Preparing report…" });
    try {
      setPdfProgress({ percent: 20, label: "Capturing dashboard…" });
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      setPdfProgress({ percent: 72, label: "Building PDF pages…" });
      const logoImage = await loadImageElement(PDF_LOGO_SRC);
      const pdf = new jsPDF("l", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const headerHeight = 20;
      const footerHeight = 8;
      const contentWidth = pageWidth - margin * 2;
      const contentHeight = pageHeight - headerHeight - footerHeight;
      const pixelsPerMm = canvas.width / contentWidth;
      const sliceHeight = Math.max(1, Math.floor(contentHeight * pixelsPerMm));

      for (let startY = 0, page = 0; startY < canvas.height; startY += sliceHeight, page += 1) {
        if (page > 0) pdf.addPage();
        const currentSliceHeight = Math.min(sliceHeight, canvas.height - startY);
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = currentSliceHeight;
        const context = sliceCanvas.getContext("2d");
        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        context.drawImage(
          canvas,
          0,
          startY,
          canvas.width,
          currentSliceHeight,
          0,
          0,
          canvas.width,
          currentSliceHeight,
        );
        pdf.addImage(
          sliceCanvas.toDataURL("image/png", 1),
          "PNG",
          margin,
          headerHeight,
          contentWidth,
          currentSliceHeight / pixelsPerMm,
          undefined,
          "FAST",
        );
      }

      setPdfProgress({ percent: 92, label: "Adding header & branding…" });
      const totalPages = pdf.getNumberOfPages();
      const generatedAt = formatPdfTimestamp(new Date());
      for (let page = 1; page <= totalPages; page += 1) {
        pdf.setPage(page);
        if (logoImage?.width && logoImage?.height) {
          const logoHeight = 12;
          const logoWidth = (logoImage.width / logoImage.height) * logoHeight;
          pdf.addImage(logoImage, "PNG", margin, 5, logoWidth, logoHeight, undefined, "FAST");
        }
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(13);
        pdf.setTextColor(31, 41, 55);
        pdf.text("Jecon Engineers Pvt. Ltd.", pageWidth - margin, 13, { align: "right" });
        pdf.setDrawColor(225, 229, 235);
        pdf.setLineWidth(0.3);
        pdf.line(margin, headerHeight - 2, pageWidth - margin, headerHeight - 2);
        pdf.line(margin, pageHeight - footerHeight + 2, pageWidth - margin, pageHeight - footerHeight + 2);
        pdf.setFontSize(8);
        pdf.setTextColor(148, 163, 184);
        
      }
      setPdfProgress({ percent: 99, label: "Saving file…" });
      pdf.save(`flange-dashboard-${projectId}.pdf`);
      setPdfProgress({ percent: 100, label: "Download ready" });
    } catch (downloadError) {
      console.error("Unable to download flange dashboard report:", downloadError);
    } finally {
      setDownloading(false);
      setPdfProgress({ percent: 0, label: "" });
    }
  };

  return (
    <main className={`flange-dashboard ${isSidebarExpanded ? "expanded" : "collapsed"}`}>
      {downloading && (
        <div className="pdf-export-overlay" role="status" aria-live="polite">
          <div className="pdf-export-modal">
            <img src="/favicon.png" alt="" aria-hidden="true" className="pdf-export-logo" />
            <p className="pdf-export-title">Preparing your dashboard PDF</p>
            <p className="pdf-export-step">{pdfProgress.label || "Working…"}</p>
            <div className="pdf-export-bar" role="progressbar" aria-valuenow={pdfProgress.percent} aria-valuemin={0} aria-valuemax={100}>
              <div className="pdf-export-bar-fill" style={{ width: `${pdfProgress.percent}%` }} />
            </div>
            <p className="pdf-export-percent">{pdfProgress.percent}%</p>
            <p className="pdf-export-hint">Please keep this tab open…</p>
          </div>
        </div>
      )}
      <div className="flange-dashboard-inner">
        <header className="flange-dashboard-header">
          <div>
            <h1>Flange Dashboard</h1>
            
          </div>
          <div className="flange-dashboard-actions">
            <form className="equipment-search-form" onSubmit={(event) => event.preventDefault()}>
              <label className="visually-hidden" htmlFor="flange-equipment-number">
                Equipment number
              </label>
              <div
                className="equipment-search-input-wrap"
                aria-busy={equipmentSearchLoading}
              >
                <Search size={15} aria-hidden="true" />
                <input
                  id="flange-equipment-number"
                  type="search"
                  value={equipmentNumber}
                  onChange={(event) => setEquipmentNumber(event.target.value)}
                  placeholder="Search equipment no."
                  autoComplete="off"
                />
                {(equipmentNumber || equipmentSummary) && (
                  <button type="button" className="equipment-search-clear" onClick={clearEquipmentSearch} aria-label="Clear equipment search">
                    <X size={14} />
                  </button>
                )}
              </div>
            </form>
            <button type="button" className="flange-dashboard-refresh" onClick={loadDashboard} disabled={loading || !projectId}>
              <RefreshCw size={15} className={loading ? "is-spinning" : ""} />
              <span className="visually-hidden">Refresh dashboard metrics</span>
            </button>
            <button type="button" className="flange-dashboard-download" onClick={downloadReport} disabled={!data || loading || downloading}>
              {downloading ? "Preparing PDF..." : "Download Dashboard Report"}
            </button>
          </div>
        </header>

        {!projectId && <div className="flange-dashboard-message">Select a project to view flange metrics.</div>}
        {projectId && error && (
          <div className="flange-dashboard-message is-error">
            <AlertTriangle size={16} /> {error}
            <button type="button" onClick={loadDashboard}>Try again</button>
          </div>
        )}
        {projectId && loading && !data && <div className="flange-dashboard-loading">Loading flange controls…</div>}

        {projectId && data && (
          <div ref={reportRef} className="flange-dashboard-report">
            {(equipmentSearchError || equipmentSummary) && (
              <div className="equipment-summary-section">
                <h2>Equipment Summary</h2>
                <section
                  className={`equipment-search-result-panel${equipmentSearchError ? " is-error" : ""}`}
                  aria-live="polite"
                >
                  {equipmentSearchError ? (
                    <p className="equipment-search-error" role="alert">
                      <AlertTriangle size={14} /> {equipmentSearchError}
                    </p>
                  ) : (
                    <div className="equipment-summary-result">
                      <div className="equipment-summary-title">
                        <span>Equipment number</span>
                        <strong>{equipmentSummary.equipment_no || searchedEquipmentNumber}</strong>
                      </div>
                      <div className="equipment-summary-stat">
                        <span>Total flanges</span>
                        <strong>{count(equipmentSummary.total_flanges)}</strong>
                      </div>
                      <div className="equipment-summary-stat">
                        <span>Active flanges</span>
                        <strong>{count(equipmentSummary.active_flanges)}</strong>
                      </div>
                      <div className="equipment-summary-stat">
                        <span>Out of scope</span>
                        <strong>{count(equipmentSummary.deleted_flanges)}</strong>
                      </div>
                    </div>
                  )}
                </section>
              </div>
            )}

            <section className="flange-dashboard-metrics" aria-label="Flange summary">
              {metrics.map((metric) => (
                <article className="flange-metric" key={metric.label}>
                  {React.createElement(metric.icon, { size: 17, "aria-hidden": true })}
                  <strong>{count(metric.value)}</strong>
                  <span>{metric.label}</span>
                </article>
              ))}
            </section>

            <section className="flange-dashboard-grid">
              <article className="flange-dashboard-panel lifecycle-panel">
                <div className="flange-panel-heading">
                  <div><h2>Lifecycle Progression</h2><p>Flange status distribution across lifecycle stages</p></div>
                  <span>{count(activeTotal)} active</span>
                </div>
                <div className="flange-lifecycle-donut-layout">
                  <div className="flange-lifecycle-chart-wrap">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={visibleLifecycleRows}
                          dataKey="value"
                          nameKey="label"
                          cx="50%"
                          cy="50%"
                          innerRadius="62%"
                          outerRadius="88%"
                          paddingAngle={2}
                          stroke="#ffffff"
                          strokeWidth={2}
                          onMouseEnter={(_, index) =>
                            setHoveredLifecycleKey(visibleLifecycleRows[index]?.key || null)
                          }
                          onMouseLeave={() => setHoveredLifecycleKey(null)}
                        >
                          {visibleLifecycleRows.map((stage) => (
                            <Cell key={stage.key} fill={stage.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div
                      className={`flange-lifecycle-center${hoveredLifecycle ? " is-hovered" : ""}`}
                      aria-live="polite"
                    >
                      {hoveredLifecycle ? (
                        <>
                          <span className="flange-lifecycle-center-label">{hoveredLifecycle.label}</span>
                          <strong>{count(hoveredLifecycle.value)}</strong>
                          <span>{hoveredLifecycle.percentage.toFixed(1)}%</span>
                        </>
                      ) : (
                        <>
                          <strong>{count(activeTotal)}</strong>
                          <span>Active Flanges</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flange-lifecycle-legend" aria-label="Lifecycle status legend">
                    {lifecycleRows.map((stage) => (
                      <div className="flange-lifecycle-legend-row" key={stage.key}>
                        <i style={{ backgroundColor: stage.color }} />
                        <span>{stage.label}</span>
                        <strong>{count(stage.value)}</strong>
                        <small>{stage.percentage.toFixed(1)}%</small>
                      </div>
                    ))}
                  </div>
                </div>
              </article>

              <article className="flange-dashboard-panel group-panel">
                <div className="flange-panel-heading">
                  <div><h2>Equipment group load</h2><p>Top groups by active flange count</p></div>
                  <span>{count(data.equipment_group_kpi?.length)} groups</span>
                </div>
                {groupRows.length ? (
                  <div className="flange-group-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={groupRows} margin={{ top: 18, right: 22, bottom: 62, left: 8 }}>
                        <XAxis
                          dataKey="name"
                          interval={0}
                          angle={-18}
                          textAnchor="end"
                          height={68}
                          minTickGap={8}
                          tickFormatter={formatGroupLabel}
                          tick={{ fontSize: 11, fill: "#5f6e7a" }}
                        />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#5f6e7a" }} />
                        <Tooltip
                          cursor={{ fill: "#f8fafc" }}
                          formatter={(value) => [count(value), "Flanges"]}
                          labelFormatter={(label) => `Equipment group: ${label}`}
                          contentStyle={{ borderRadius: 10, border: "1px solid #d8e3f0", boxShadow: "0 6px 20px rgba(28,55,90,.12)" }}
                        />
                        <Bar dataKey="count" maxBarSize={72} radius={[6, 6, 0, 0]}>
                          {groupRows.map((row, index) => (
                            <Cell key={row.name} fill={["#991b1b", "#b91c1c", "#d02b2b", "#e05252", "#ef7b7b", "#f2a3a3"][index % 6]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : <div className="flange-panel-empty">No equipment group data available.</div>}
              </article>
            </section>

            <section className="flange-dashboard-panel flange-activity-panel">
              <div className="flange-panel-heading">
                <div><h2>Recent Flange Activities</h2><p>Latest flange lifecycle updates in this project</p></div>
                <span>{count(recentActivities.length)} activities</span>
              </div>
              {recentActivities.length ? (
                <div className="flange-activity-list">
                  {recentActivities.map((activity, index) => (
                    <button
                      type="button"
                      className="flange-activity-row"
                      key={`${activity.activity_type}-${activity.occurred_at}-${index}`}
                      onClick={() => openFlangeInList(activity.subtitle)}
                    >
                      <span className={`flange-activity-dot is-${activity.activity_type || "default"}`} />
                      <span className="flange-activity-main">
                        <strong>{activity.title || "Flange updated"}</strong>
                        <small>
                          {activity.subtitle || "Flange"}
                          {activity.equipment_number ? ` · ${activity.equipment_number}` : ""}
                          {activity.actor ? ` · ${activity.actor}` : ""}
                        </small>
                      </span>
                      <span className="flange-activity-status">{activity.status || "Updated"}</span>
                      <time>{formatDate(activity.occurred_at)}</time>
                      <ExternalLink size={14} aria-hidden="true" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flange-panel-empty flange-activity-empty">
                  <Activity size={18} /> No flange activities recorded yet.
                </div>
              )}
            </section>

            <section className="flange-dashboard-panel closure-panel">
              <div className="flange-panel-heading">
                <div><h2>Ready to close</h2></div>
                <span>{count(data.ready_to_close?.length)} records</span>
              </div>
              {data.ready_to_close?.length ? (
                <div className="flange-closure-scroll">
                  <table className="flange-closure-table">
                    <thead><tr><th>Flange ID</th><th>Equipment</th><th>Current status</th><th>Test</th><th>Last updated</th><th aria-label="Actions" /></tr></thead>
                    <tbody>
                      {data.ready_to_close.map((flange) => (
                        <tr key={flange.flange_id}>
                          <td><button type="button" onClick={() => openFlangeInList(flange.flange_number)}>{flange.flange_number || "—"}</button></td>
                          <td>{flange.equipment_number || "Unassigned"}</td>
                          <td><span className="flange-status-label">{flange.status || "—"}</span></td>
                          <td><span className="flange-pass-label"><CheckCircle2 size={12} /> {flange.test_result}</span></td>
                          <td className="flange-date-cell">{formatDate(flange.last_updated)}</td>
                          <td><button type="button" className="flange-open-row" onClick={() => openFlangeInList(flange.flange_number)} aria-label={`Find ${flange.flange_number} in flange list`}><ExternalLink size={14} /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flange-panel-empty closure-empty"><CheckCircle2 size={18} /> No passed flanges are waiting for client approval.</div>
              )}
              <button type="button" className="flange-view-all" onClick={() => navigate("/flanges")}>
                View all flanges <ArrowRight size={14} />
              </button>
            </section>
          </div>
        )}
      </div>
    </main>
  );
}
